import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { getClusters, putClusters, getIncentives, putIncentives } from "@/services/ccApi";
import { DEFAULT_INCENTIVE } from "@/services/configStore";
import { text } from "@/lib/format";

const uid = () => "cl-" + Math.random().toString(36).slice(2, 8);
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

export default function Incentives() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "clusters";
  const pros = useCollection("professionals");

  const [clusters, setClusters] = useState([]);
  const [rules, setRules] = useState(DEFAULT_INCENTIVE);
  const [clOpen, setClOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", lead_pro_ids: [] });

  const reload = () => { getClusters().then(setClusters).catch(() => {}); getIncentives().then((r) => setRules({ ...DEFAULT_INCENTIVE, ...r })).catch(() => {}); };
  useEffect(() => { reload(); }, []);

  const prosByCluster = useMemo(() => { const m = {}; pros.forEach((p) => { if (p.cluster) (m[p.cluster] ||= []).push(p); }); return m; }, [pros]);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", lead_pro_ids: [] }); setClOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, description: c.description || "", lead_pro_ids: c.lead_pro_ids || [] }); setClOpen(true); };

  const saveCluster = async () => {
    if (!form.name.trim()) return toast.error("Cluster name required");
    const list = editing ? clusters.map((c) => (c.id === editing ? { ...c, ...form, name: form.name.trim() } : c)) : [...clusters, { id: uid(), ...form, name: form.name.trim() }];
    try { const saved = await putClusters(list); setClusters(saved); toast.success("Cluster saved"); setClOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeCluster = async (id) => { try { const saved = await putClusters(clusters.filter((c) => c.id !== id)); setClusters(saved); toast.success("Cluster removed"); } catch (e) { toast.error(errMsg(e)); } };
  const toggleLead = (proId) => setForm((f) => ({ ...f, lead_pro_ids: f.lead_pro_ids.includes(proId) ? f.lead_pro_ids.filter((x) => x !== proId) : [...f.lead_pro_ids, proId] }));

  const saveRules = async () => { try { const saved = await putIncentives(rules); setRules({ ...DEFAULT_INCENTIVE, ...saved }); toast.success("Incentive rules saved"); } catch (e) { toast.error(errMsg(e)); } };

  const RULE_FIELDS = [
    ["per_completed_job", "₹ per completed job"],
    ["per_brugo_promise", "₹ per Brugo Promise project"],
    ["response_rate_target", "Response-rate target (%)"],
    ["response_rate_bonus", "₹ bonus if rate ≥ target"],
    ["top_pro_cluster_bonus", "₹ top pro in each cluster"],
    ["max_promise_cluster_bonus", "₹ most Brugo Promise in cluster"],
    ["cluster_lead_bonus", "₹ cluster lead professional"],
  ];

  return (
    <div>
      <PageHeader title="Incentives & Clusters" subtitle="Define operational clusters, assign lead professionals, and set incentive rules. The Performance page uses these to calculate every pro's payout. Cluster assignment on a pro is saved to Firebase; definitions & rules are stored in the Command Center." />

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mb-4">
        <TabsList>
          <TabsTrigger value="clusters" data-testid="inc-tab-clusters">Clusters ({clusters.length})</TabsTrigger>
          <TabsTrigger value="rules" data-testid="inc-tab-rules">Incentive Rules</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "clusters" ? (
        <Card>
          <div className="p-4 border-b border-border flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Assign pros to a cluster from the Professionals page after creating it here.</p>
            <Dialog open={clOpen} onOpenChange={setClOpen}>
              <DialogTrigger asChild><Button size="sm" className="gap-1.5" onClick={openNew} data-testid="add-cluster-btn"><Icons.Plus className="h-4 w-4" /> Add Cluster</Button></DialogTrigger>
              <DialogContent data-testid="cluster-dialog" className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Cluster</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="space-y-1.5"><Label>Cluster name</Label><Input data-testid="cluster-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Gurgaon Central" /></div>
                  <div className="space-y-1.5"><Label>Description (optional)</Label><Textarea data-testid="cluster-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div className="space-y-1.5">
                    <Label>Lead professional(s)</Label>
                    <div className="max-h-40 overflow-y-auto rounded-lg border border-border p-2 space-y-1">
                      {(prosByCluster[form.name] || []).map((p) => (
                        <label key={p.id} className="flex items-center gap-2 text-sm px-1 py-1 cursor-pointer">
                          <input type="checkbox" checked={form.lead_pro_ids.includes(p.id)} onChange={() => toggleLead(p.id)} data-testid={`lead-${p.id}`} />
                          {text(p.full_name || p.business_name, p.id)}
                        </label>
                      ))}
                      {(prosByCluster[form.name] || []).length === 0 && <p className="text-xs text-muted-foreground px-1 py-1">No pros assigned to this cluster yet. Save it, then assign pros from the Professionals page.</p>}
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={saveCluster} data-testid="save-cluster">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Cluster</TableHead><TableHead>Description</TableHead><TableHead>Pros</TableHead><TableHead>Lead pros</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {clusters.map((c) => (
                <TableRow key={c.id} data-testid={`cluster-row-${c.id}`}>
                  <TableCell className="font-medium text-foreground">{c.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{text(c.description)}</TableCell>
                  <TableCell className="font-mono text-sm">{(prosByCluster[c.name] || []).length}</TableCell>
                  <TableCell className="font-mono text-sm">{(c.lead_pro_ids || []).length}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-cluster-${c.id}`}><Icons.Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeCluster(c.id)} data-testid={`remove-cluster-${c.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {clusters.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No clusters yet. Add one.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="p-6 max-w-2xl">
          <h3 className="font-heading font-semibold text-foreground mb-1">Incentive rules</h3>
          <p className="text-sm text-muted-foreground mb-4">These values feed the payout column on the Performance page. Amounts in ₹.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {RULE_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label>{label}</Label>
                <Input type="number" data-testid={`rule-${k}`} value={rules[k]} onChange={(e) => setRules({ ...rules, [k]: Number(e.target.value) || 0 })} />
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-lg border border-border bg-secondary/50 p-3 text-xs text-muted-foreground">
            Example payout per pro = (₹{rules.per_completed_job}/job × jobs) + (₹{rules.per_brugo_promise} × promise projects) + response-rate bonus + cluster bonuses.
          </div>
          <Button className="mt-4 gap-1.5" onClick={saveRules} data-testid="save-rules"><Icons.Save className="h-4 w-4" /> Save rules</Button>
        </Card>
      )}
    </div>
  );
}
